import React, { useEffect, useRef, useState, VFC } from 'react'
import * as AutoKana from 'vanilla-autokana'
import AddressSearch from '../../common/form/AddressSearch'
import styles from '../../styles/front/form/Form.module.sass'
import useFormInputStartTracking from './useFormInputStartTracking'

// gtag用の拡張型定義
declare global {
  interface Window {
    gtag: (event: string, action: string, params: {}) => void
  }
}

type Prefecture = {
  prefecture_name: string
  prefecture_code: string
}

type CommonForm = {
  name: string
  name_kana?: string
  tel: string
  email?: string
  zipcode?: string
  prefecture_code?: string
  city_name?: string
  address?: string
  extra_request?: string
  company_name?: string
  job_title?: string
}

type FormTemplateProps = {
  prefectures?: Prefecture[]
  common_form: CommonForm
  errors?: { [key: string]: string[] }
  form_type: string
  isFormValid: any
  isShowNameKana?: boolean
  isShowAddress?: boolean
  isShowCompanyName?: boolean
  isShowJobTitle?: boolean
  placeHolderText: string
  check: number
  form_keys?: string[]
  optional_keys?: string[]
}

type City = {
  [cities: string]: [string]
}

let autokana
let timer

const FormTemplate: VFC<FormTemplateProps> = (props: FormTemplateProps) => {
  const {
    prefectures,
    common_form,
    errors,
    form_type,
    isFormValid,
    isShowNameKana = true,
    isShowAddress = true,
    isShowCompanyName = false,
    isShowJobTitle = false,
    placeHolderText = '',
    form_keys = [],
    optional_keys = [],
  } = props

  const MaxNameLength = 30
  const MaxExtraRequestLength = 300
  const nameRef = useRef<HTMLInputElement>(null)
  const kanaRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState(common_form?.name ?? '')
  const [nameKana, setNameKana] = useState(common_form?.name_kana ?? '')
  const [tel, setTel] = useState(common_form?.tel ?? '')
  const [email, setEmail] = useState(common_form?.email ?? '')
  const [zipcode, setZipcode] = useState(common_form?.zipcode ?? '')
  const [prefectureCode, setPrefectureCode] = useState<string>(common_form?.prefecture_code ?? '')
  const [cityName, setCityName] = useState<string>(common_form?.city_name ?? '')
  const [address, setAddress] = useState<string>(common_form?.address ?? '')
  const [extraRequest, setExtraRequest] = useState<string>(common_form?.extra_request ?? '')
  const [companyName, setCompanyName] = useState<string>(common_form?.company_name ?? '')
  const [jobTitle, setJobTitle] = useState<string>(common_form?.job_title ?? '')

  const [relatedCities, setRelatedCities] = useState<City>()
  const [errorMsg, setErrorMsg] = useState<string>('')

  const [isEmptyName, setIsEmptyName] = useState(!common_form ? false : common_form.name ? false : true)
  const [isCompleteName, setIsCompleteName] = useState(false)
  const [isFirstInputName, setIsFirstInputName] = useState(!common_form ? true : common_form.name ? false : true)

  const [isValidCompanyName, setIsValidCompanyName] = useState(!errors ? false : errors.company_name ? false : true)
  const [isFirstInputCompanyName, setIsFirstInputCompanyName] = useState(
    !common_form ? true : common_form.company_name ? false : true
  )
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState('')

  const [isFirstInputNameKana, setIsFirstInputNameKana] = useState(true)
  const [isCompleteNameKana, setIsCompleteNameKana] = useState(false)
  const [isValidKana, setIsValidKana] = useState(!errors ? false : errors.name_kana ? false : true)

  const [isCompleteTel, setIsCompleteTel] = useState(false)
  const [isFirstInputTel, setIsFirstInputTel] = useState(true)
  const [isValidTel, setIsValidTel] = useState(!errors ? false : errors.tel ? false : true)

  const [isCompleteEmail, setIsCompleteEmail] = useState(false)
  const [isFirstInputEmail, setIsFirstInputEmail] = useState(true)
  const [isValidEmail, setIsValidEmail] = useState(!errors ? false : errors.email ? false : true)

  const [isFirstInputZipcode, setIsFirstInputZipcode] = useState(true)
  const [isCompleteZipcode, setIsCompleteZipcode] = useState(false)
  const [isValidZipcode, setIsValidZipcode] = useState(!errors ? false : errors.zipcode ? false : true)

  const [isEmptyAddress, setIsEmptyAddress] = useState(!common_form ? false : common_form.address ? false : true)
  const [isFirstInputAddress, setIsFirstInputAddress] = useState(
    !common_form ? true : common_form.address ? false : true
  )
  const [isCompleteAddress, setIsCompleteAddress] = useState(false)
  const [requiresAddressEdit, setRequiresAddressEdit] = useState(false)

  const [isFirstSelectPrefectureCode, setIsFirstSelectPrefectureCode] = useState(true)
  const [isFirstSelectCityName, setIsFirstSelectCityName] = useState(true)

  const [nameErrorMsg, setNameErrorMsg] = useState('')
  const [kanaErrorMsg, setKanaErrorMsg] = useState('')
  const [telErrorMsg, setTelErrorMsg] = useState('')
  const [emailErrorMsg, setEmailErrorMsg] = useState('')
  const [zipCodeErrorMsg, setZipCodeErrorMsg] = useState('')
  const [addressErrorMsg, setAddressErrorMsg] = useState('')
  const [isValidPrefectureCode, setIsValidPrefectureCode] = useState(true)
  const [isVaildCityName, setIsVaildCityName] = useState(true)

  const [timerRunning, setTimerRunning] = useState(false)

  const telRegexp = /^(0{1}\d{9,10})$/
  const mailRegexp = /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/
  const zipcodeRegexp = /^\d{7}$/
  const kanaRegexp = /^[ァ-ーｦ-ﾟ　 ]+$/
  const templateNameErrorMsg = '入力してください'
  const templateKanaErrorMsg = 'カタカナで入力してください'
  const templatePhoneNumberFormatErrorMsg = '半角数字で入力してください'
  const templateMailFormatErrorMsg = 'メールアドレスの形式で入力してください'
  const templateAddressErrorMsg = '町名以降を入力してください'
  const templateZipCodeFormatErrorMsg = '半角数字7ケタで入力してください'

  //　チェック対象のキーかどうか
  const isCheckKey = (key) => {
    if (form_keys.length === 0) return true
    return form_keys.includes(key)
  }

  const isOptionalKey = (key) => optional_keys.includes(key)
  const isRequiredKey = (key) => isCheckKey(key) && !isOptionalKey(key)
  const isAddressRequired =
    isRequiredKey('zipcode') || isRequiredKey('prefecture_code') || isRequiredKey('city_name') || isRequiredKey('address')
  const showNameError = (!!errors?.name && errors.name.length > 0) || (!isFirstInputName && isEmptyName)
  const showAddressError =
    (!!errors?.address && errors.address.length > 0) || (!isFirstInputAddress && isEmptyAddress) || requiresAddressEdit

  let addressResult = { prefectureCode: '', cityName: '', townName: '' }

  useEffect(() => {
    autokana = AutoKana.bind('#name', '#name_kana', { katakana: true })
    if (common_form) {
      AddressSearch.setCities(common_form.prefecture_code, setRelatedCitiesData)
    }
  }, [])

  // DOM要素取得のヘルパー関数（重複を避けるため）
  function getKanaInput(): HTMLInputElement | null {
    return document.getElementById('name_kana') as HTMLInputElement | null
  }

  // カナのバリデーションと状態更新を共通化
  function updateKanaState(kanaValue: string, shouldUpdateFirstInput: boolean = true) {
    const normalizedValue = kanaValue.normalize('NFKC').replace(/\u00A0/g, ' ')
    const isValid = normalizedValue !== '' && kanaRegexp.test(normalizedValue)
    setIsValidKana(isValid)
    setNameKana(normalizedValue)
    if (isValid) {
      setKanaErrorMsg('')
    } else {
      if (!isFirstInputNameKana) {
        setKanaErrorMsg(normalizedValue === '' ? templateNameErrorMsg : templateKanaErrorMsg)
      }
    }
    if (shouldUpdateFirstInput && normalizedValue !== '') {
      setIsFirstInputNameKana(false)
    }
  }

  // autokanaでフリガナが反映されるタイミングが遅いので名前入力中はtimerをセットしてフリガナの値を取得する
  function setFuriganaCheckTimer() {
    const kana = getKanaInput()
    if (kana) {
      timer = setInterval(() => {
        updateKanaState(kana.value)
      }, 200)
    }
  }

  function inputName(v) {
    if (!timerRunning) {
      setTimerRunning(true)
      setFuriganaCheckTimer()
    }
    setIsFirstInputName(false)
    let nameCurrent = nameRef.current

    if (!nameCurrent) return

    nameCurrent.value = v
    setName(v)

    setIsEmptyName(v === '')
    if (v === '') {
      setNameErrorMsg(templateNameErrorMsg)
      setIsValidKana(false)
      setNameKana('')
      if (!isFirstInputNameKana) {
        setKanaErrorMsg(templateNameErrorMsg)
      }
    } else {
      setNameErrorMsg('')
    }
  }

  function checkInputName(value) {
    // 名前のfocusが外れた時にtimerクリア
    clearInterval(timer)
    setTimerRunning(false)
    setName(value)
    setIsCompleteName(true)
    setIsCompleteNameKana(true)

    // Windows環境でのAutoKana遅延対策: タイマー停止後もDOM値を直接取得してstateを更新
    const kanaInput = getKanaInput()
    if (kanaInput) {
      updateKanaState(kanaInput.value, false)

      // さらに短いディレイ後にもう一度チェック（AutoKanaの最終更新を確実に拾う）
      setTimeout(() => {
        const finalKanaInput = getKanaInput()
        if (finalKanaInput?.value) {
          updateKanaState(finalKanaInput.value, false)
        }
      }, 100)
    }
  }

  function inputNameKana(v: string) {
    setIsFirstInputNameKana(false)
    updateKanaState(v, false)
  }

  function checkInputNameKana(value: string) {
    updateKanaState(value, false)
    setIsCompleteNameKana(true)
  }

  function inputTel(v) {
    setIsFirstInputTel(false)

    const regexp = telRegexp
    const isValid = v === '' ? !isRequiredKey('tel') : regexp.test(v)
    setIsValidTel(isValid)
    setTel(v)
    if (isValid) {
      setTelErrorMsg('')
    } else {
      setTelErrorMsg(templatePhoneNumberFormatErrorMsg)
    }
  }

  function checkInputTel(value) {
    setTel(value)
    setIsCompleteTel(true)
  }

  function inputEmail(v) {
    setIsFirstInputEmail(false)

    const regexp = mailRegexp
    const isValid = v === '' ? !isRequiredKey('email') : regexp.test(v)
    setIsValidEmail(isValid)
    setEmail(v)
    if (isValid) {
      setEmailErrorMsg('')
    } else {
      setEmailErrorMsg(templateMailFormatErrorMsg)
    }
  }

  function checkInputEmail(value) {
    setEmail(value)
    setIsCompleteEmail(true)
  }

  function handleChangeZipcode(value) {
    setIsFirstInputZipcode(false)

    const regexp = zipcodeRegexp
    const isValid = value === '' ? !isRequiredKey('zipcode') : regexp.test(value)
    setIsValidZipcode(isValid)
    setZipcode(value)
    if (isValid) {
      setZipCodeErrorMsg('')
      if (value !== '') {
        setAllAddressData(value)
        setIsValidPrefectureCode(true)
        setIsVaildCityName(true)
        setIsEmptyAddress(false)
        setIsFirstInputAddress(false)
        setAddressErrorMsg('')
        setIsValidPrefectureCode(true)
        setIsVaildCityName(true)
        setIsFirstSelectPrefectureCode(false)
        setIsFirstSelectCityName(false)
      }
    } else {
      setZipCodeErrorMsg(templateZipCodeFormatErrorMsg)
      setIsValidPrefectureCode(prefectureCode !== '' || !isRequiredKey('prefecture_code'))
      setIsVaildCityName(cityName !== '' || !isRequiredKey('city_name'))
      if (address === '' && isRequiredKey('address')) {
        setIsEmptyAddress(true)
        setAddressErrorMsg(templateAddressErrorMsg)
      }
    }
  }

  function handleBlurZipcode(value) {
    setZipcode(value)
    setIsCompleteZipcode(true)
  }

  function handleChangePrefectureCode(input_value) {
    setPrefectureCode(input_value)
    AddressSearch.setCities(input_value, setRelatedCitiesData)
    setIsValidPrefectureCode(true)
    setIsFirstSelectPrefectureCode(false)
    if (input_value === '') {
      setCityName('')
      setIsVaildCityName(true)
      setIsFirstSelectCityName(true)
    } else {
      setIsVaildCityName(false)
      setIsFirstSelectCityName(false)
    }
  }

  function handleChangeCityName(input_value) {
    setCityName(input_value)
    setIsVaildCityName(input_value !== '')
    setIsFirstSelectCityName(false)
  }

  function handleChangeAddress(input_value) {
    if (isFirstInputAddress) setIsFirstInputAddress(false)
    if (requiresAddressEdit) setRequiresAddressEdit(false)
    setAddress(input_value)
    if (input_value === '') {
      setIsEmptyAddress(isRequiredKey('address'))
      setAddressErrorMsg(isRequiredKey('address') ? templateAddressErrorMsg : '')
      setIsCompleteAddress(false)
    } else {
      setIsEmptyAddress(false)
      setAddressErrorMsg('')
    }
  }

  function handleBlurAddress(value) {
    if (value !== '') {
      setAddress(value)
      setIsCompleteAddress(true)
    }
  }

  const setAddressResult = (addressSearchResult) => {
    setPrefectureCode(addressSearchResult.prefectureCode)
    setCityName(addressSearchResult.cityName)
    setAddress(addressSearchResult.townName)
    addressResult = addressSearchResult
    setRequiresAddressEdit(true)
    setIsCompleteAddress(false)

    if (addressSearchResult.townName && addressSearchResult.townName !== '') {
      document.getElementById('address').focus()
    }

  }

  const setRelatedCitiesData = (value) => {
    setRelatedCities(value)
  }

  // 郵便番号検索を行った結果のエラーメッセージを設定
  const setSearchAddressErrorMsg = (zipCodeErrorMessage) => {
    setErrorMsg(zipCodeErrorMessage)

    if (zipCodeErrorMessage) {
      setIsValidZipcode(false)
    }

    /**
     * useStateを使用している関係で前回の値しか参照出来ないので
     * addressResultを使用して郵便番号検索で取得した結果を参照できるようにしている）
     */
    if (addressResult.prefectureCode === '' && prefectureCode === '' && isRequiredKey('prefecture_code') && !isValidPrefectureCode) {
      setIsValidPrefectureCode(false)
    }

    if (addressResult.cityName === '' && cityName === '' && isRequiredKey('city_name')) {
      setIsVaildCityName(false)
    } else {
      setIsVaildCityName(true)
    }

    if (addressResult.townName === '' && address === '' && isRequiredKey('address')) {
      setIsEmptyAddress(true)
      setAddressErrorMsg(templateAddressErrorMsg)
    }
  }

  const setAllAddressData = (value: string) => {
    AddressSearch.setFullAddress(
      value,
      prefectures,
      setAddressResult,
      setRelatedCitiesData,
      setSearchAddressErrorMsg,
      'front'
    )
  }

  function handleChangeCompanyName(value: string) {
    setIsFirstInputCompanyName(false)
    setCompanyName(value)
    const isValid = value !== ''
    setIsValidCompanyName(isValid)
    if (isValid) {
      setCompanyNameErrorMsg('')
    } else {
      setCompanyNameErrorMsg(templateNameErrorMsg)
    }
  }

  function handleChangeJobTitle(value: string) {
    setJobTitle(value)
  }

  function checkBlank() {
    if (name == '') {
      setNameErrorMsg(templateNameErrorMsg)
      setIsEmptyName(true)
      setIsFirstInputName(false)
    }
    if (isShowCompanyName && companyName == '') {
      setCompanyNameErrorMsg(templateNameErrorMsg)
      setIsValidCompanyName(false)
      setIsFirstInputCompanyName(false)
    }
    if (nameKana == '' && isRequiredKey('name_kana')) {
      setKanaErrorMsg(templateNameErrorMsg)
      setIsValidKana(false)
      setIsFirstInputNameKana(false)
    }
    if (tel == '' && isRequiredKey('tel')) {
      setTelErrorMsg(templatePhoneNumberFormatErrorMsg)
      setIsValidTel(false)
      setIsFirstInputTel(false)
    }
    if (email == '' && isRequiredKey('email')) {
      setEmailErrorMsg(templateMailFormatErrorMsg)
      setIsValidEmail(false)
      setIsFirstInputEmail(false)
    }
    if (isShowAddress) {
      if (zipcode == '' && isRequiredKey('zipcode')) {
        setZipCodeErrorMsg(templateZipCodeFormatErrorMsg)
        setIsValidZipcode(false)
        setIsFirstInputZipcode(false)
      }
      if (address === '' && isRequiredKey('address')) {
        setAddressErrorMsg(templateAddressErrorMsg)
        setIsEmptyAddress(true)
        setIsFirstInputAddress(false)
      }
      if (prefectureCode === '' && isRequiredKey('prefecture_code')) {
        setIsValidPrefectureCode(false)
        setIsFirstSelectPrefectureCode(false)
      }
      if (cityName === '' && isRequiredKey('city_name')) {
        setIsVaildCityName(false)
        setIsFirstSelectCityName(false)
      }
    }
  }

  useEffect(() => {
    if (props.check != 0) {
      checkBlank()
    }
    // 補足事項項目をカスタム項目の下に表示
    const extraRequestContent = document.getElementById('extra_request_content')
    const customContents = document.getElementById('customContents')
    if (extraRequestContent && customContents) {
      customContents.after(extraRequestContent)
    }
  }, [props.check])

  function setValidParams() {
    // 基本必須項目のチェック
    const telValidation = !isCheckKey('tel') || (isRequiredKey('tel') ? isValidTel : tel === '' || isValidTel)
    const emailValidation = !isCheckKey('email') || (isRequiredKey('email') ? isValidEmail : email === '' || isValidEmail)
    const baseValidation = !isEmptyName && telValidation && emailValidation

    // フリガナのチェック
    const kanaValidation = !isShowNameKana || !isCheckKey('name_kana') || (isRequiredKey('name_kana') ? isValidKana : nameKana === '' || isValidKana)

    let zipcodeValidation: boolean
    let prefectureValidation: boolean
    let cityValidation: boolean
    let addressValidationValue: boolean

    zipcodeValidation = !isCheckKey('zipcode') || (isRequiredKey('zipcode') ? isValidZipcode : zipcode === '' || isValidZipcode)
    prefectureValidation =
      !isCheckKey('prefecture_code') || (isRequiredKey('prefecture_code') ? prefectureCode !== '' : prefectureCode === '' || isValidPrefectureCode)
    cityValidation =
      !isCheckKey('city_name') || (isRequiredKey('city_name') ? cityName !== '' : cityName === '' || isVaildCityName)
    addressValidationValue =
      !isCheckKey('address') ||
      (isRequiredKey('address') ? address !== '' && !requiresAddressEdit : address === '' || !requiresAddressEdit)

    // 住所関連項目のチェック
    const addressValidation =
      !isShowAddress ||
      (zipcodeValidation && prefectureValidation && cityValidation && addressValidationValue)

    // 会社名のバリデーション
    const companyValidation = !isShowCompanyName || isValidCompanyName

    // 全体のバリデーション
    const isValid = baseValidation && kanaValidation && addressValidation && companyValidation

    isFormValid(isValid)
  }

  useEffect(() => {
    setValidParams()
  }, [
    isEmptyName,
    isValidKana,
    isValidTel,
    isValidEmail,
    isValidZipcode,
    zipcode,
    nameKana,
    prefectureCode,
    cityName,
    address,
    requiresAddressEdit,
    companyName,
    isValidPrefectureCode,
    isVaildCityName,
  ])

  useFormInputStartTracking()

  return (
    <>
      {isShowCompanyName && (
        <div className={styles.inputContainer}>
          <label className={styles.label}>
            <div className={styles.labelTitle}>会社名</div>
            <div className={styles.labelTag + ' ' + styles.required}>必須</div>
          </label>
          <div className={styles.inputArea}>
            <input
              type="text"
              name={`${form_type}[company_name]`}
              value={companyName}
              placeholder={'株式会社山田工務店'}
              className={`${styles.formInput} ${
                !isValidCompanyName && !isFirstInputCompanyName
                  ? styles.errorInput
                  : isValidCompanyName
                  ? styles.inputColor
                  : ''
              }`}
              onChange={(e) => handleChangeCompanyName(e.target.value)}
              required
            />
            {isValidCompanyName && companyName && (
              <div className={styles.success}>
                <span className={'material-icons-outlined'}>done</span>
              </div>
            )}
            {!isFirstInputCompanyName && !isValidCompanyName && (
              <div className={styles.error}>
                <span className={'material-icons-outlined'}>close</span>
              </div>
            )}
            {companyNameErrorMsg && <div className={styles.errorText}>{companyNameErrorMsg}</div>}
            {errors && errors.company_name && (
              <>
                {errors.company_name.map((error: string, index: number) => (
                  <div className={styles.errorText} key={index}>
                    {error}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
      {isShowJobTitle && (
        <div className={styles.inputContainer}>
          <label className={styles.label}>
            <div className={styles.labelTitle}>役職</div>
            <div className={styles.labelTag + ' ' + styles.optional}>任意</div>
          </label>
          <div className={styles.inputArea}>
            <input
              type="text"
              name={`${form_type}[job_title]`}
              value={jobTitle}
              placeholder={'部長'}
              className={styles.formInput}
              onChange={(e) => handleChangeJobTitle(e.target.value)}
            />
          </div>
        </div>
      )}
      <div className={styles.inputContainer}>
        <label htmlFor="name" className={styles.label}>
          <div className={styles.labelTitle}>お名前</div>
          <div className={styles.labelTag + ' ' + styles.required}>必須</div>
        </label>
        <div className={styles.inputArea}>
          <input
            id="name"
            name={`${form_type}[name]`}
            type="text"
            placeholder="山田　太郎"
            value={name}
            ref={nameRef}
            maxLength={MaxNameLength}
            onChange={(e) => inputName(e.target.value)}
            onBlur={(e) => checkInputName(e.target.value)}
            className={
              styles.formInput +
              ' ' +
              (!isEmptyName && !isFirstInputName
                ? styles.inputColor
                : showNameError
                ? styles.errorInput + ' error'
                : '')
            }
          />
          {name && isCompleteName && (
            <div className={styles.success}>
              <span className={'material-icons-outlined'}>done</span>
            </div>
          )}
          {!isFirstInputName && !name && isCompleteName && (
            <div className={styles.error}>
              <span className={'material-icons-outlined'}>close</span>
            </div>
          )}
          {nameErrorMsg && <div className={styles.errorText}>{nameErrorMsg}</div>}
          {errors && errors.name && (
            <>
              {errors.name.map((error, index) => (
                <div className={styles.errorText} key={index}>
                  {error}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <div className={styles.note + ' ' + styles.halfNumber}>※姓と名の間にスペースを入力してください</div>
      {isShowNameKana && isCheckKey('name_kana') && (
        <>
          <div className={styles.inputContainer}>
            <label htmlFor="name_kana" className={styles.label}>
              <div className={styles.labelTitle}>フリガナ</div>
              <div className={styles.labelTag + ' ' + (isRequiredKey('name_kana') ? styles.required : styles.optional)}>
                {isRequiredKey('name_kana') ? '必須' : '任意'}
              </div>
            </label>
            <div className={styles.inputArea}>
              <input
                id="name_kana"
                name={`${form_type}[name_kana]`}
                type="text"
                onChange={(e) => inputNameKana(e.target.value)}
                onBlur={(e) => checkInputNameKana(e.target.value)}
                placeholder="ヤマダ　タロウ"
                defaultValue={nameKana}
                ref={kanaRef}
                maxLength={MaxNameLength}
                className={
                  styles.formInput +
                  ' ' +
                  ((errors?.name_kana && !isValidKana) || (!isValidKana && !isFirstInputNameKana)
                    ? styles.errorInput + ' error'
                    : isValidKana
                    ? styles.inputColor
                    : '')
                }
              />
              {isValidKana && isCompleteNameKana && (
                <div className={styles.success}>
                  <span className={'material-icons-outlined'}>done</span>
                </div>
              )}
              {!isFirstInputNameKana && !isValidKana && isCompleteNameKana && (
                <div className={styles.error}>
                  <span className={'material-icons-outlined'}>close</span>
                </div>
              )}
              {kanaErrorMsg && <div className={styles.errorText}>{kanaErrorMsg}</div>}
              {errors && errors.name_kana && (
                <>
                  {errors.name_kana.map((error, index) => (
                    <div className={styles.errorText} key={index}>
                      {error}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className={styles.note + ' ' + styles.halfNumber}>※姓と名の間にスペースを入力してください</div>
        </>
      )}
      {isCheckKey('tel') && (
        <>
          <div className={styles.inputContainer}>
            <label htmlFor="tel" className={styles.label}>
              <div className={styles.labelTitle}>電話番号</div>
              <div className={styles.labelTag + ' ' + (isRequiredKey('tel') ? styles.required : styles.optional)}>
                {isRequiredKey('tel') ? '必須' : '任意'}
              </div>
            </label>
            <div className={styles.inputArea}>
              <input
                id="tel"
                name={`${form_type}[tel]`}
                type="tel"
                inputMode="numeric"
                pattern="\d*"
                placeholder="例：09012345678"
                value={tel}
                onChange={(e) => inputTel(e.target.value)}
                onBlur={(e) => checkInputTel(e.target.value)}
                className={
                  styles.formInput +
                  ' ' +
                  ((errors?.tel && !isValidTel) || (!isValidTel && !isFirstInputTel)
                    ? styles.errorInput + ' error'
                    : isValidTel
                    ? styles.inputColor
                    : '')
                }
              />
              {isValidTel && isCompleteTel && (
                <div className={styles.success}>
                  <span className={'material-icons-outlined'}>done</span>
                </div>
              )}
              {!isFirstInputTel && !isValidTel && isCompleteTel && (
                <div className={styles.error}>
                  <span className={'material-icons-outlined'}>close</span>
                </div>
              )}
              {telErrorMsg && <div className={styles.errorText}>{telErrorMsg}</div>}
              {errors && errors.tel && (
                <>
                  {errors.tel.map((error, index) => (
                    <div className={styles.errorText} key={index}>
                      {error}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className={styles.note + ' ' + styles.halfNumber}>※半角数字・ハイフン不要</div>
        </>
      )}
      {isCheckKey('email') && (
        <>
          <div className={styles.inputContainer}>
            <label htmlFor="email" className={styles.label}>
              <div className={styles.labelTitle}>メールアドレス</div>
              <div className={styles.labelTag + ' ' + (isRequiredKey('email') ? styles.required : styles.optional)}>
                {isRequiredKey('email') ? '必須' : '任意'}
              </div>
            </label>
            <div className={styles.inputArea}>
              <input
                id="email"
                name={`${form_type}[email]`}
                type="text"
                placeholder="yamada@mail.com"
                value={email}
                onChange={(e) => inputEmail(e.target.value)}
                onBlur={(e) => checkInputEmail(e.target.value)}
                className={
                  styles.formInput +
                  ' ' +
                  ((errors?.email && !isValidEmail) || (!isValidEmail && !isFirstInputEmail)
                    ? styles.errorInput + ' error'
                    : isValidEmail
                    ? styles.inputColor
                    : '')
                }
              />
              {isValidEmail && isCompleteEmail && (
                <div className={styles.success}>
                  <span className={'material-icons-outlined'}>done</span>
                </div>
              )}
              {!isFirstInputEmail && !isValidEmail && isCompleteEmail && (
                <div className={styles.error}>
                  <span className={'material-icons-outlined'}>close</span>
                </div>
              )}
              {emailErrorMsg && <div className={styles.errorText}>{emailErrorMsg}</div>}
              {errors && errors.email && (
                <>
                  {errors.email.map((error, index) => (
                    <div className={styles.errorText} key={index}>
                      {error}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className={styles.note + ' ' + styles.halfNumber}>※半角英数字</div>
        </>
      )}
      {isShowAddress && (isCheckKey('zipcode') || isCheckKey('prefecture_code')) && (
        <div className={styles.inputContainer + ' ' + styles.multipleInput}>
          <div className={styles.label + ' ' + styles.addressPcLabel}>
            <div className={styles.labelTitle}>ご住所</div>
            <div className={styles.labelTag + ' ' + (isAddressRequired ? styles.required : styles.optional)}>
              {isAddressRequired ? '必須' : '任意'}
            </div>
          </div>
          <div className={styles.addressSpLabel}>
            <div className={styles.label}>
              <div className={styles.labelTitle}>お住まい</div>
              <div className={styles.labelTag + ' ' + (isAddressRequired ? styles.required : styles.optional)}>
                {isAddressRequired ? '必須' : '任意'}
              </div>
            </div>
            {isCheckKey('zipcode') && (
              <a
                href="https://www.post.japanpost.jp/zipcode/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link + ' ' + styles.searchZipcodeSp}
              >
                郵便番号検索
              </a>
            )}
          </div>
          <div>
            {isCheckKey('zipcode') && (
            <div className={styles.multipleInputContainer}>
              <label htmlFor="zipcode" className={styles.note + ' ' + styles.smallInputTitle}>
                郵便番号
              </label>
              <div className={styles.smallInputContainer}>
                <div className={styles.inputArea}>
                  <input
                    id="zipcode"
                    name={`${form_type}[zipcode]`}
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    placeholder="1080073"
                    onChange={(e) => handleChangeZipcode(e.target.value)}
                    onBlur={(e) => handleBlurZipcode(e.target.value)}
                    value={zipcode}
                    className={
                      styles.formInput +
                      ' ' +
                      styles.formSamallInput +
                      ' ' +
                      ((errors?.zipcode && !isValidZipcode) || (!isValidZipcode && !isFirstInputZipcode)
                        ? styles.errorInput + ' error'
                        : isValidZipcode
                        ? styles.inputColor
                        : '')
                    }
                  />
                  {isValidZipcode && isCompleteZipcode && (
                    <div className={styles.success}>
                      <span className={'material-icons-outlined'}>done</span>
                    </div>
                  )}
                  {!isFirstInputZipcode && !isValidZipcode && isCompleteZipcode && (
                    <div className={styles.error}>
                      <span className={'material-icons-outlined'}>close</span>
                    </div>
                  )}
                  {errorMsg && <div className={styles.errorText}>{errorMsg}</div>}
                  {zipCodeErrorMsg && <div className={styles.errorText}>{zipCodeErrorMsg}</div>}
                  {errors && errors.zipcode && (
                    <>
                      {errors.zipcode.map((error, index) => (
                        <div className={styles.errorText} key={index}>
                          {error}
                        </div>
                      ))}
                    </>
                  )}
                  <div className={styles.note + ' ' + styles.zipcodeNotice}>※半角数字・ハイフン不要</div>
                </div>
              </div>
              <a
                href="https://www.post.japanpost.jp/zipcode/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link + ' ' + styles.searchZipcodePc}
              >
                郵便番号検索
              </a>
            </div>
            )}

            {isCheckKey('prefecture_code') && (
            <div className={styles.multipleInputContainer}>
              <label htmlFor="prefecture_code" className={styles.note + ' ' + styles.smallInputTitle}>
                都道府県
              </label>
              <div className={styles.formSelectBox}>
                <select
                  onChange={async (e) => {
                    await handleChangePrefectureCode(e.target.value)
                  }}
                  value={prefectureCode}
                  className={
                    isFirstSelectPrefectureCode
                      ? ''
                      : isValidPrefectureCode
                      ? styles.inputColor
                      : styles.errorInput + ' error'
                  }
                >
                  <option hidden value={''}>
                    都道府県を選択
                  </option>
                  {prefectures.map((prefecture, index) => (
                    <option value={prefecture[1]} key={`prefecture_${index}`}>
                      {prefecture[0]}
                    </option>
                  ))}
                </select>
                {errors && errors.prefecture_code && (
                  <>
                    {errors.prefecture_code.map((error, index) => (
                      <div className={styles.errorText} key={index}>
                        {error}
                      </div>
                    ))}
                  </>
                )}
              </div>
              <input type="hidden" id="prefecture_code" name={`${form_type}[prefecture_code]`} value={prefectureCode} />
            </div>
            )}

            {isCheckKey('city_name') && (
            <div className={styles.multipleInputContainer}>
              <label htmlFor="city_name" className={styles.note + ' ' + styles.smallInputTitle}>
                市区町村
              </label>
              <div className={styles.formSelectBox}>
                <select
                  onChange={(e) => {
                    handleChangeCityName(e.target.value)
                  }}
                  value={cityName}
                  className={
                    isFirstSelectCityName ? '' : isVaildCityName ? styles.inputColor : styles.errorInput + ' error'
                  }
                >
                  <option hidden value={''}>
                    市区町村を選択
                  </option>
                  {relatedCities &&
                    relatedCities.cities.map((city, index) => (
                      <option value={city} key={`city_${index}`}>
                        {city}
                      </option>
                    ))}
                </select>
                {errors && errors.city_name && (
                  <>
                    {errors.city_name.map((error, index) => (
                      <div className={styles.errorText} key={index}>
                        {error}
                      </div>
                    ))}
                  </>
                )}
              </div>
              <input type="hidden" id="city_name" name={`${form_type}[city_name]`} value={cityName} />
            </div>
            )}

            {isCheckKey('address') && (
            <div className={styles.multipleInputContainer}>
              <label htmlFor="address" className={styles.note + ' ' + styles.smallInputTitle}>
                町名以降
              </label>
              <div className={styles.smallInputContainer}>
                <div className={styles.inputArea}>
                  <input
                    id="address"
                    name={`${form_type}[address]`}
                    type="text"
                    placeholder="町名・番地/建物・部屋番号"
                    value={address}
                    onChange={(e) => handleChangeAddress(e.target.value)}
                    onBlur={(e) => handleBlurAddress(e.target.value)}
                    className={
                      styles.formInput +
                      ' ' +
                      styles.formSamallInput +
                      ' ' +
                      (!isEmptyAddress && !isFirstInputAddress && !requiresAddressEdit
                        ? styles.inputColor
                        : showAddressError
                        ? styles.errorInput + ' error'
                        : '')
                    }
                  />
                  {isCompleteAddress && address && !requiresAddressEdit && (
                    <div className={styles.success}>
                      <span className={'material-icons-outlined'}>done</span>
                    </div>
                  )}
                  {isCompleteAddress && !address && (
                    <div className={styles.error}>
                      <span className={'material-icons-outlined'}>close</span>
                    </div>
                  )}
                  {addressErrorMsg && <div className={styles.errorText}>{addressErrorMsg}</div>}
                  {errors && errors.address && (
                    <>
                      {errors.address.map((error, index) => (
                        <div className={styles.errorText} key={index}>
                          {error}
                        </div>
                      ))}
                    </>
                  )}
                  <div
                    className={`${styles.note} ${styles.zipcodeNotice} ${requiresAddressEdit ? styles.noteError : ''}`}
                  >
                    ※番地以降もご記入ください
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      )}
      {isCheckKey('extra_request') && (
        <div className={styles.inputContainer} id="extra_request_content">
          <label htmlFor="extra_request" className={styles.label}>
            <div className={styles.labelTitle}>補足事項</div>
            <div className={styles.labelTag + ' ' + styles.optional}>任意</div>
          </label>
          <textarea
            id="extra_request"
            name={`${form_type}[extra_request]`}
            value={extraRequest}
            onChange={(e) => setExtraRequest(e.target.value)}
            placeholder={placeHolderText}
            maxLength={MaxExtraRequestLength}
          />
        </div>
      )}
    </>
  )
}

export default FormTemplate
